import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Animated,
  StyleSheet,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useTypography } from '../hooks/useTypography';
import { withAlpha } from '../theme';

export interface DropdownOption {
  value: string;
  label: string;
  selected?: boolean;
}

interface DropdownProps {
  label: string;
  value: string | string[];
  options: DropdownOption[];
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (value: string) => void;
  placeholder?: string;
  multiSelect?: boolean;
  maxHeight?: number;
  displayValue?: (value: string | string[]) => string;
  style?: any;
  wrapperStyle?: any;
  isScrollingRef?: React.MutableRefObject<boolean>;
}

export const Dropdown: React.FC<DropdownProps> = ({
  label,
  value,
  options,
  isOpen,
  onToggle,
  onSelect,
  placeholder = 'All',
  multiSelect = false,
  maxHeight = 300,
  displayValue,
  style,
  wrapperStyle,
  isScrollingRef,
}) => {
  const wrapperRef = useRef<View>(null);
  const animValue = useRef(new Animated.Value(0)).current;
  const rtl = useRTLStyles();
  const typo = useTypography();

  useEffect(() => {
    Animated.timing(animValue, {
      toValue: isOpen ? 1 : 0,
      duration: 200,
      useNativeDriver: false,
    }).start();
  }, [isOpen, animValue]);

  const getDisplayText = () => {
    if (displayValue) {
      return displayValue(value);
    }
    if (multiSelect && Array.isArray(value)) {
      if (value.length === 0) return placeholder;
      if (value.length === 1) {
        const option = options.find(opt => opt.value === value[0]);
        return option?.label || value[0];
      }
      return `${value.length} selected`;
    }
    if (!multiSelect && typeof value === 'string') {
      const option = options.find(opt => opt.value === value);
      return option?.label || value;
    }
    return placeholder;
  };

  const handleScrollBegin = () => {
    if (isScrollingRef) {
      isScrollingRef.current = true;
    }
  };

  const handleScrollEnd = () => {
    if (isScrollingRef) {
      setTimeout(() => {
        if (isScrollingRef) {
          isScrollingRef.current = false;
        }
      }, 100);
    }
  };

  const styles = createStyles();
  const dynamicOptionStyle = { flexDirection: rtl.rowDirection as any };
  const dynamicTextStyle = { textAlign: rtl.textStart as any, fontFamily: typo.fontFamily.primary };
  const dynamicLabelStyle = { textAlign: rtl.textStart as any, fontFamily: typo.fontFamily.medium };
  const dynamicValueContainerStyle = { flexDirection: rtl.rowDirection as any };

  return (
    <View 
      ref={wrapperRef}
      style={[styles.wrapper, wrapperStyle]}
      collapsable={false}
      data-dropdown-wrapper="true"
    >
      <TouchableOpacity
        style={[styles.button, isOpen && styles.buttonOpen, style]}
        onPress={(e) => {
          if (Platform.OS === 'web') {
            if (e?.stopPropagation) {
              e.stopPropagation();
            }
            if (e?.nativeEvent?.stopPropagation) {
              e.nativeEvent.stopPropagation();
            }
          }
          onToggle();
        }}
        activeOpacity={0.7}
        {...(Platform.OS === 'web' && {
          // @ts-ignore - web-specific prop
          onClick: (e: any) => {
            e?.stopPropagation?.();
          },
        })}
      >
        <Text style={[styles.label, dynamicLabelStyle]}>{label}</Text>
        <View style={[styles.valueContainer, dynamicValueContainerStyle]}>
          <Text style={[styles.valueText, dynamicTextStyle]} numberOfLines={1}>
            {getDisplayText()}
          </Text>
          <Ionicons
            name={isOpen ? "chevron-up" : "chevron-down"}
            size={18}
            color={colors.textSecondary}
          />
        </View>
      </TouchableOpacity>

      <Animated.View
        style={[
          styles.dropdown,
          {
            opacity: animValue,
            height: animValue.interpolate({
              inputRange: [0, 1],
              outputRange: [0, maxHeight],
            }),
            display: isOpen ? 'flex' : 'none',
            transform: [{
              translateY: animValue.interpolate({
                inputRange: [0, 1],
                outputRange: [-10, 0],
              }),
            }],
          },
        ]}
        data-dropdown-list="true"
        {...(Platform.OS === 'web' && {
          // @ts-ignore - web-specific prop
          onClick: (e: any) => {
            e?.stopPropagation?.();
          },
          onMouseDown: (e: any) => {
            e?.stopPropagation?.();
          },
        })}
      >
        {isOpen && (
          <ScrollView
            nestedScrollEnabled
            style={{ height: maxHeight }}
            contentContainerStyle={{ paddingBottom: spacing.xs }}
            showsVerticalScrollIndicator={true}
            scrollEnabled={true}
            onScrollBeginDrag={handleScrollBegin}
            onScrollEndDrag={handleScrollEnd}
            onMomentumScrollEnd={handleScrollEnd}
            onStartShouldSetResponder={() => true}
            onResponderTerminationRequest={() => false}
            {...(Platform.OS === 'web' && {
              // @ts-ignore - web-specific prop
              onClick: (e: any) => {
                e?.stopPropagation?.();
              },
              onMouseDown: (e: any) => {
                e?.stopPropagation?.();
              },
            })}
          >
            {options.map((option) => {
              const isSelected = multiSelect
                ? Array.isArray(value) && value.includes(option.value)
                : value === option.value;

              return (
                <TouchableOpacity
                  key={option.value}
                  style={[styles.option, isSelected && styles.optionActive, dynamicOptionStyle]}
                  onPress={() => {
                    onSelect(option.value);
                  }}
                  activeOpacity={0.7}
                  {...(Platform.OS === 'web' && {
                    // @ts-ignore - web-specific prop
                    onClick: (e: any) => {
                      // Stop all event propagation immediately to prevent document listener from closing dropdown
                      if (e?.stopPropagation) e.stopPropagation();
                      if (e?.stopImmediatePropagation) e.stopImmediatePropagation();
                      if (e?.preventDefault) e.preventDefault();
                      // Also handle native event
                      if (e?.nativeEvent) {
                        if (e.nativeEvent.stopPropagation) e.nativeEvent.stopPropagation();
                        if (e.nativeEvent.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation();
                        if (e.nativeEvent.preventDefault) e.nativeEvent.preventDefault();
                      }
                    },
                    onMouseDown: (e: any) => {
                      // Also stop mousedown to prevent document listener from firing
                      if (e?.stopPropagation) e.stopPropagation();
                      if (e?.stopImmediatePropagation) e.stopImmediatePropagation();
                    },
                  })}
                >
                  {multiSelect ? (
                    <Ionicons
                      name={isSelected ? 'checkbox' : 'square-outline'}
                      size={20}
                      color={isSelected ? colors.primary : colors.textSecondary}
                    />
                  ) : (
                    isSelected && (
                      <Ionicons name="checkmark" size={16} color={colors.primary} />
                    )
                  )}
                  <Text
                    style={[
                      styles.optionText,
                      isSelected && styles.optionTextActive,
                      dynamicTextStyle,
                    ]}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}
      </Animated.View>
    </View>
  );
};

const createStyles = () =>
  StyleSheet.create({
    wrapper: {
      flex: 1,
      minWidth: 120,
      position: 'relative',
      zIndex: 10000,
      elevation: 20,
      ...(Platform.OS === 'web' && {
        pointerEvents: 'auto',
      }),
    },
    button: {
      backgroundColor: colors.background.primary,
      borderRadius: designTokens.borderRadius.md,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.sm,
    },
    buttonOpen: {
      borderColor: colors.primary,
      borderWidth: 2,
    },
    label: {
      fontSize: typography.fontSize.xs,
      color: colors.textSecondary,
      marginBottom: spacing.xs / 2,
    },
    valueContainer: {
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    valueText: {
      fontSize: typography.fontSize.sm,
      color: colors.text.primary,
      flex: 1,
      marginRight: spacing.xs,
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      marginTop: spacing.xs,
      backgroundColor: colors.background.surface,
      borderRadius: designTokens.borderRadius.md,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: colors.textDark,
      shadowOffset: { width: 0, height: 12 },
      shadowOpacity: 0.25,
      shadowRadius: 24,
      zIndex: 10001,
      elevation: 25,
      overflow: 'hidden',
      ...(Platform.OS !== 'web' && {
        minWidth: 280,
        width: 'auto',
      }),
      ...(Platform.OS === 'web' && {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        position: 'absolute',
        minWidth: 280,
      }),
    },
    option: {
      alignItems: 'center',
      gap: spacing.sm,
      padding: spacing.sm,
      paddingHorizontal: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      width: '100%',
      minWidth: '100%',
    },
    optionActive: {
      backgroundColor: withAlpha(colors.primary, 0.06),
    },
    optionText: {
      fontSize: typography.fontSize.sm,
      color: colors.text.primary,
      flex: 1,
      flexShrink: 0,
      flexWrap: 'nowrap',
    },
    optionTextActive: {
      color: colors.primary,
      fontWeight: typography.fontWeight.medium,
    },
  });
